import { Component, Host, Listen, Prop, h, Element, Watch, State } from '@stencil/core';
import { HTMLTiTooltipTriggerElement } from '@ticom/ui-components/dist';

@Component({
    tag: 'ti-product-status',
    styleUrl: 'product-status.scss',
    shadow: true
})
export class ProductStatus {

    private static readonly _CHECK_PATH = 'M96,8a88,88,0,1,0,88,88A88.032,88.032,0,0,0,96,8ZM84.932,136.072,78.4,142.6l-.009-.008-.008.008L74.9,139.119l-38.6-38.4L50.424,86.6,78.375,114.4,138.842,54.11l14.122,14.122Z';
    private static readonly _EXCLAMATION_PATH = 'm 104 192 a 88 88 90 1 0 -88 -88 a 88.033 88.033 90 0 0 88 88 z m -8 -132 l 16 0 l 0 56 l -16 0 l 0 -56 z m 0 72 l 16 0 l 0 16 l -16 0 l 0 -16 z';

    public static readonly ACTIVE: number = 1;
    public static readonly LIFEBUY: number = 8;
    public static readonly NRND: number = 3;
    public static readonly OBSOLETE: number = 4;
    public static readonly PREVIEW: number = 5;

    private _linkElementAttrs: Record<string, unknown>;
    private _statusIconPath = ProductStatus._CHECK_PATH;
    private _tooltipTriggerEl: HTMLTiTooltipTriggerElement;

    @Element() hostElement: HTMLTiProductStatusElement;

    /**
     * Store the text extracted from the slotted link element so we can re-use it.
     *
     * @type {string}
     * @memberof ProductStatus
     */
    @State() statusText: string;

    /**
     * Display a compact version of this component.
     *
     * @type {boolean}
     * @memberof ProductStatus
     */
    @Prop({ reflect: true }) compact: boolean = false;

    /**
     * The status is an integer representing one of the following:
     *
     *     1: active
     *     8: lifebuy
     *     3: nrnd
     *     4: obsolete
     *     5: preview
     *
     * @type {number}
     * @default ProductStatus.ACTIVE
     */
    @Prop({ reflect: true }) status: number = ProductStatus.ACTIVE;

    /**
     * Listen to the `blur` event on host-element.
     * We need this for keyboard interaction becuase we're focusing the outer link, not the tooltip trigger.
     */
    @Listen('blur', {capture: true})
    onBlur() {
        if (this.tooltip) {
            this._tooltipTriggerEl.closeTooltip();
        }
    }

    /**
     * Listen to the `focus` event on host-element.
     * We need this for keyboard interaction becuase we're focusing the outer link, not the tooltip trigger.
     */
    @Listen('focus', {capture: true})
    onFocus() {
        if (this.tooltip) {
            this._tooltipTriggerEl.openTooltip();
        }
    }

    /**
     * Listen to the `keyup` event on host-element.
     * We need this for keyboard interaction becuase we're focusing the outer link, not the tooltip trigger.
     */
    @Listen('keyup')
    onKeyup(ev: KeyboardEvent) {
        if (this.tooltip && ev.code === 'Escape') {
            ev.preventDefault();
            this._tooltipTriggerEl.closeTooltip();
        }
    }

    @Watch('status')
    handleStatusChange() {
        this._setStatusIcon();
        window.setTimeout(() => this._setStatusText());
    }

    /**
     * Text to display in the tooltip for the product status badge.
     * If a toolTip is not passed in and a product is ACTIVE DE-EMPHASIZED, NRND, LIFEBUY, or OBSOLETE a default toolTip will be provided.
     * The details can be found in the _setToolTip() function.
     * @type {string}
     */
    @Prop({ mutable: true }) tooltip: string;

    componentWillLoad() {
        this._setStatusText();
        this._setStatusIcon();
    }

    private  _setStatusIcon() {
        // set the status icon
        if (!this.status) {
            this._statusIconPath = null;
        } else if (ProductStatus.ACTIVE === this.status) {
            this._statusIconPath = ProductStatus._CHECK_PATH;
        } else {
            this._statusIconPath = ProductStatus._EXCLAMATION_PATH;
        }
    }

    private _setStatusText() {
        this.hostElement.querySelectorAll('a').forEach(linkElement => {
            if (!linkElement.classList.contains('ti-product-status-link')) {
                // save all the link element's attributes to reapply to our internal link element
                if (linkElement.attributes && linkElement.attributes.length) {
                    const attrObject = {};
                    this._linkElementAttrs = attrObject;
                    for (let i = 0; i < linkElement.attributes.length; i++) {
                        const attr = linkElement.attributes[i];
                        attrObject[attr.name] = attr.value;
                    }
                }

                // save the content of the link element to use as our content
                this.statusText = linkElement.innerText;

                // hide the link
                linkElement.classList.add('ti-product-status-link-hide');
            }
        });
    }

    render() {
        let styleClass = 'ti-product-status-';
        switch (this.status) {
            case ProductStatus.ACTIVE:
                styleClass += 'active';
                break;
            case ProductStatus.LIFEBUY:
                styleClass += 'lifebuy';
                break;
            case ProductStatus.NRND:
                styleClass += 'nrnd';
                break;
            case ProductStatus.OBSOLETE:
                styleClass += 'obsolete';
                break;
            case ProductStatus.PREVIEW:
                styleClass += 'preview';
                break;
            default:
                return null;
        }

        if (this.compact) {
            styleClass += ' ti-product-status-compact';
        }

        let content = [
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 192 192" aria-hidden="true">
                <path
                    class={!document.head.attachShadow && 'sc-ti-product-status'} // workaround for IE
                    d={this._statusIconPath}
                />
            </svg>,

            /* we need this span to force IE11 to respect the target property of the anchor
            tag when clicking on the status text when deployed (not a local dev issue) */
            <span innerHTML={this.statusText}></span>,

            <slot />
        ];

        if (this.tooltip) {
            content =
                <ti-tooltip-trigger class="ti-product-status-container" tabindex="-1" ref={el => this._tooltipTriggerEl = el}>
                    {content}
                    <ti-tooltip position="bottom">{this.tooltip}</ti-tooltip>
                </ti-tooltip-trigger>
            ;
        }

        return (
            <Host class={ styleClass }>
                <div class='ti-product-status-container'>
                    {this._linkElementAttrs
                        ?
                        <a {...this._linkElementAttrs} class="ti-product-status-link">
                            {content}
                        </a>
                        :
                        content
                    }
                </div>
            </Host>
        );
    }
}
